/**
 * ui/SchemaForm — rendu générique d'une section de config piloté par schéma.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * UI — candidat `@mostajs/app-admin-ui-react` (N2). Ne connaît AUCUN champ métier :
 * il rend les champs décrits par l'`AdminSchema`. Types : text/textarea/number/
 * boolean/color/select/localized (D12) + **repeater** (listes d'objets :
 * services, guichets…). À terme, remplaçable par le formulaire auto de @mostajs/settings.
 */
import React, { useState, useEffect } from 'react';
import { getPath } from '../engine/paths';
import { LOCALES } from '../engine/i18n';

const ROLES = ['admin', 'manager', 'agent', 'viewer'];

// Libellés des flèches d'ordre du repeater. Trilingues comme tout le reste de la
// console : ces boutons sont vus par des exploitants qui ne lisent pas le français.
const L_MONTER = { ar: 'رفع', fr: 'Monter', en: 'Move up' };
const L_DESCENDRE = { ar: 'خفض', fr: 'Descendre', en: 'Move down' };

/** Contrôle d'édition d'UNE valeur (sans notion de chemin) — réutilisable. */
function InputControl({ type, value, onChange, options, t }) {
  switch (type) {
    case 'localized': {
      const obj = value && typeof value === 'object' ? value : {};
      return (
        <div className="localized">
          {LOCALES.map((loc) => (
            <input
              key={loc}
              dir={loc === 'ar' ? 'rtl' : 'ltr'}
              placeholder={loc.toUpperCase()}
              value={obj[loc] ?? ''}
              onChange={(e) => onChange({ ...obj, [loc]: e.target.value })}
            />
          ))}
        </div>
      );
    }
    case 'boolean':
      return <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />;
    case 'number':
      return <input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'color':
      return <input type="color" value={value ?? '#000000'} onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="" />
          {(options || []).map((o) => (
            <option key={o.value} value={o.value}>{t.localized(o.label, o.value)}</option>
          ))}
        </select>
      );
    case 'textarea':
      return <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'text':
    default:
      return <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
}

/**
 * Éditeur de liste d'objets (repeater) : ordre + ajout/suppression + sous-champs.
 *
 * Les flèches ↑/↓ permutent deux éléments du TABLEAU, et c'est tout : nulle part dans
 * TicketFlow il n'existe de champ « ordre » — l'ordre d'affichage EST celui du tableau
 * de config (guichets sur /counter, services sur /distributor et /display, où le rendu
 * fait un `map` direct). L'ordre lu ici est donc l'ordre vu par le public, sans clé
 * supplémentaire à saisir ni à tenir cohérente.
 */
function Repeater({ field, value, onChange, t }) {
  const items = Array.isArray(value) ? value : [];
  const setItems = (next) => onChange(next);

  const updateItem = (idx, key, v) =>
    setItems(items.map((it, i) => (i === idx ? { ...it, [key]: v } : it)));
  const addItem = () => setItems([...items, { ...(field.itemDefaults || {}) }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  /** Permute l'élément `idx` avec son voisin (`sens` = -1 monter, +1 descendre). */
  const moveItem = (idx, sens) => {
    const cible = idx + sens;
    if (cible < 0 || cible >= items.length) return;
    const next = items.slice();
    [next[idx], next[cible]] = [next[cible], next[idx]];
    setItems(next);
  };

  return (
    <div className="repeater">
      {items.length === 0 && <p className="muted">—</p>}
      {items.map((item, idx) => (
        // La CLÉ est l'INDEX, pas l'id : ces listes tolèrent les ids en double (le parc
        // en compte), et deux lignes de même clé se marchent dessus à la permutation —
        // on verrait les champs d'un guichet sauter sur un autre.
        <div className="repeater-item" key={idx}>
          <div className="repeater-fields">
            {field.itemFields.map((f) => (
              <div className="field" key={f.key}>
                <label>{t.localized(f.label, f.key)}</label>
                <InputControl
                  type={f.type}
                  value={item[f.key]}
                  options={f.options}
                  t={t}
                  onChange={(v) => updateItem(idx, f.key, v)}
                />
              </div>
            ))}
          </div>
          <div className="repeater-actions">
            <button
              type="button"
              className="ghost"
              title={t.localized(L_MONTER, 'Monter')}
              disabled={idx === 0}
              onClick={() => moveItem(idx, -1)}
            >↑</button>
            <button
              type="button"
              className="ghost"
              title={t.localized(L_DESCENDRE, 'Descendre')}
              disabled={idx === items.length - 1}
              onClick={() => moveItem(idx, +1)}
            >↓</button>
            <button type="button" className="ghost danger" onClick={() => removeItem(idx)}>✕</button>
          </div>
        </div>
      ))}
      <button type="button" className="add" onClick={addItem}>＋ {t.localized(field.addLabel, 'Ajouter')}</button>
    </div>
  );
}

/** Action d'upload de fichier (multipart) vers un endpoint déclaré (ex. publicité). */
function MediaUpload({ field, api, reload, t }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async () => {
    if (!file || !api) return;
    setBusy(true);
    setMsg('');
    try {
      // JSON base64 D'ABORD : le relais console→site ré-encode le corps en JSON, un
      // `FormData` n'y survit pas — c'est le seul transport qui fonctionne à distance.
      //
      // MAIS un parc ne se met pas à jour d'un bloc : un serveur pas encore réinstallé ne
      // connaît pas `dataUrl` et refuse. On retombe alors sur le multipart, qui marche en
      // réseau local. Sans ce repli, la console neuve casse les sites anciens — c'est
      // exactement ce qui est arrivé en déployant la console avant les serveurs.
      const dataUrl = await readAsDataUrl(file);
      try {
        await api.raw.post(field.endpoint, { dataUrl, filename: file.name, title, titleAr: title });
      } catch (jsonErr) {
        const st = jsonErr?.response?.status;
        if (st === 403 || st === 401) throw jsonErr;     // droits : le repli n'y changera rien
        await api.upload(field.endpoint, file, { title, titleAr: title });
      }
      setFile(null);
      setTitle('');
      setMsg('✓');
      await reload?.();
    } catch (e) {
      // Le serveur explique lui-même le refus — notamment qu'une vidéo trop lourde doit
      // être déposée depuis TicketFlow-admin en réseau local. Ne pas l'écraser.
      const st = e?.response?.status;
      setMsg(st === 403 ? 'Accès refusé (rôle)' : String(e?.response?.data?.error || e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="media-upload">
      <input type="file" accept={field.accept || '*'} onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <input type="text" placeholder={t.localized(field.label, 'Titre')} value={title} onChange={(e) => setTitle(e.target.value)} />
      <button type="button" onClick={submit} disabled={busy || !file}>⬆</button>
      {msg && <span className="status">{msg}</span>}
    </div>
  );
}

/** Lit un fichier local en data-URL base64 (`data:image/png;base64,…`). */
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(file);
  });
}

/**
 * Champ IMAGE : URL éditable + aperçu + dépôt d'un fichier (D1 — l'admin est la
 * seule surface d'écriture).
 *
 * Le fichier part en JSON base64, pas en multipart : depuis la console centrale
 * l'appel traverse le relais WebSocket du plan de contrôle, qui ré-encode le
 * corps en JSON. Le même code sert donc les deux façades — admin.exe en LAN et
 * la console à distance — au lieu de deux chemins qui divergeraient en silence.
 *
 * L'URL rendue par le serveur est POSÉE DANS LE FORMULAIRE, pas enregistrée :
 * c'est « Enregistrer » qui la persiste, avec le reste de l'onglet.
 */
function ImageUpload({ field, value, onChange, api, t }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [broken, setBroken] = useState(false);
  const url = typeof value === 'string' ? value : '';

  // Une URL relative désigne un fichier du SERVEUR DE SITE. En LAN (`apiBase` = son
  // adresse) l'aperçu s'affiche ; depuis la console, `apiBase` est le chemin du relais
  // — l'image n'est pas joignable, et on le dit plutôt que de laisser une icône cassée.
  const preview = /^(https?:|data:|blob:)/i.test(url) ? url : `${api?.apiBase || ''}${url}`;

  const pick = async (file) => {
    if (!file || !api) return;
    setBusy(true); setMsg('');
    try {
      const dataUrl = await readAsDataUrl(file);
      const { data } = await api.raw.post(field.endpoint, { dataUrl, filename: file.name });
      const next = data?.data?.url;
      if (!next) throw new Error('Réponse inattendue du serveur');
      setBroken(false);
      onChange(next);
      setMsg('✓ déposé — pensez à enregistrer');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setMsg('Accès refusé (rôle)');
      else if (status === 413) setMsg('Image trop lourde');
      else setMsg(String(e?.response?.data?.error || e?.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="image-upload">
      <div className="image-upload-row">
        <input
          type="text"
          value={url}
          placeholder="/logo.png"
          onChange={(e) => { setBroken(false); onChange(e.target.value); }}
        />
        <input
          type="file"
          accept={field.accept || 'image/*'}
          disabled={busy}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>
      {url && !broken && (
        <img className="image-upload-preview" src={preview} alt={t.localized(field.label, 'Image')} onError={() => setBroken(true)} />
      )}
      {url && broken && (
        <span className="muted">Aperçu indisponible ici (fichier hébergé par le serveur du site) — {url}</span>
      )}
      {(busy || msg) && <span className="status">{busy ? '…' : msg}</span>}
    </div>
  );
}

/** Grille rôles × permissions (D11) éditant l'objet `rolePermissions`. */
function PermissionMatrix({ field, value, onChange, t }) {
  const roles = field.roles || [];
  const perms = field.permissions || [];
  const defaults = field.defaults || {};
  const matrix = value && typeof value === 'object' ? value : {};
  // État effectif d'un rôle : override config sinon défauts (visible & éditable).
  const effective = (role) => (Array.isArray(matrix[role]) ? matrix[role] : (defaults[role] || []));
  const toggle = (role, code) => {
    const cur = effective(role);
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    onChange({ ...matrix, [role]: next });
  };
  return (
    <table className="perm-matrix">
      <thead>
        <tr>
          <th />
          {perms.map((p) => <th key={p.code}>{t.localized(p.label, p.code)}</th>)}
        </tr>
      </thead>
      <tbody>
        {roles.map((role) => (
          <tr key={role}>
            <td>{role}</td>
            {perms.map((p) => (
              <td key={p.code}>
                <input type="checkbox" checked={effective(role).includes(p.code)} onChange={() => toggle(role, p.code)} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Gestion des comptes (CRUD via /api/users) — n'édite pas la config. */
function UsersManager({ api }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', fullName: '', role: 'agent' });
  const [msg, setMsg] = useState('');

  const refresh = () => api.listUsers().then(setUsers).catch((e) => setMsg(String(e?.message || e)));
  useEffect(() => { refresh(); /* au montage */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wrap = (fn) => async (...a) => {
    setMsg('');
    try { await fn(...a); await refresh(); } catch (e) {
      setMsg(e?.response?.status === 403 ? 'Accès refusé (rôle)' : String(e?.response?.data?.error || e?.message || e));
    }
  };
  const create = wrap(async () => {
    if (!form.username || !form.password || !form.fullName) { setMsg('Champs requis'); return; }
    await api.createUser(form);
    setForm({ username: '', password: '', fullName: '', role: 'agent' });
  });
  const setRole = wrap((u, role) => api.updateUser(u.id, { role }));
  const deactivate = wrap((u) => api.deactivateUser(u.id));
  const del = wrap((u) => { if (window.confirm(`Supprimer ${u.username} ?`)) return api.deleteUser(u.id); });

  return (
    <div className="users-manager">
      <table className="perm-matrix">
        <thead><tr><th>User</th><th>Nom</th><th>Rôle</th><th>Actif</th><th /></tr></thead>
        <tbody>
          {!users && <tr><td colSpan="5" className="muted">…</td></tr>}
          {users && users.map((u) => (
            <tr key={u.id}>
              <td>{u.username}</td>
              <td>{u.fullName}</td>
              <td>
                <select value={u.role} onChange={(e) => setRole(u, e.target.value)}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </td>
              <td>{u.isActive === false ? '✗' : '✓'}</td>
              <td>
                <button type="button" className="ghost" onClick={() => deactivate(u)} title="Désactiver">⏸</button>
                <button type="button" className="ghost danger" onClick={() => del(u)} title="Supprimer">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="user-create">
        <input placeholder="username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <input placeholder="nom complet" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        <input type="password" placeholder="mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="button" onClick={create}>＋ créer</button>
      </div>
      {msg && <div className="status">{msg}</div>}
    </div>
  );
}

/** Vue LECTURE SEULE d'un endpoint (stats, historique) — scalaires + 1er tableau. */
function ApiView({ field, api }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.raw.get(field.endpoint)
      .then((r) => setData(r.data?.data ?? r.data))
      .catch((e) => setMsg(String(e?.response?.data?.error || e?.message || e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (msg) return <div className="status">{msg}</div>;
  if (!data) return <p className="muted">…</p>;
  const entries = Object.entries(data);
  const scalars = entries.filter(([, v]) => v == null || typeof v !== 'object');
  const arr = entries.find(([, v]) => Array.isArray(v));
  const rows = arr ? arr[1] : [];
  const cols = rows.length ? Object.keys(rows[0]) : [];
  return (
    <div className="api-view">
      {scalars.length > 0 && (
        <div className="stat-chips">
          {scalars.map(([k, v]) => <span key={k} className="chip"><b>{String(v)}</b> {k}</span>)}
        </div>
      )}
      {rows.length > 0 && (
        <table className="perm-matrix">
          <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.slice(0, 100).map((r, i) => (
              <tr key={r.id ?? i}>{cols.map((c) => <td key={c}>{typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c] ?? '')}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Sélecteur de FORME + MODE des carrés de services → thème actif (config.themes[activeThemeId].serviceCard). */
const ORGANIC = ['vigne', 'cerises', 'raisins'];
function ServiceCardShape({ field, data, onChange, t }) {
  const activeId = data?.activeThemeId || 'default';
  const themes = Array.isArray(data?.themes) ? data.themes : [];
  const active = themes.find((x) => x.id === activeId);
  const sc = active?.serviceCard || {};
  const current = sc.shape || 'rectangle';
  const mode = sc.motifMode || 'coin';

  const patchCard = (patch) => {
    const apply = (x) => ({ ...x, serviceCard: { ...(x.serviceCard || {}), ...patch } });
    const next = active
      ? themes.map((x) => (x.id === activeId ? apply(x) : x))
      : [...themes, apply({ id: activeId })];
    onChange(next); // écrit tout le tableau `themes`
  };

  return (
    <div className="shape-picker-wrap">
      <div className="shape-picker">
        {(field.options || []).map((o) => (
          <button
            key={o.value}
            type="button"
            className={current === o.value ? 'shape-btn active' : 'shape-btn'}
            onClick={() => patchCard({ shape: o.value })}
          >
            {t.localized(o.label, o.value)}
          </button>
        ))}
      </div>
      {ORGANIC.includes(current) && (
        <div className="shape-picker" style={{ marginTop: '.5rem' }}>
          <span className="muted">Affichage :</span>
          {[['coin', 'Motif (coin)'], ['plein', 'Image pleine (chiffre dessus)']].map(([m, lbl]) => (
            <button key={m} type="button" className={mode === m ? 'shape-btn active' : 'shape-btn'} onClick={() => patchCard({ motifMode: m })}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      {/* Tailles des fiches de services (/display). Vide = valeur par défaut du thème. */}
      <div className="card-sizes">
        <span className="muted">Tailles des fiches (écran /display) :</span>
        <div className="size-grid">
          {[
            ['countSize', 'Nombre en attente', '4.5rem'],
            ['nameSize', 'Nom du service', '1.5rem'],
            ['waitingSize', "Texte d'attente", '1rem'],
            ['minWidth', 'Largeur mini fiche', '240px'],
            ['columns', 'Colonnes (auto/2/3…)', 'auto'],
            ['padding', 'Marge interne', '2rem'],
            ['gap', 'Espacement fiches', '1.5rem'],
          ].map(([key, lbl, ph]) => (
            <label key={key} className="size-field">
              <span>{lbl}</span>
              <input value={sc[key] ?? ''} placeholder={ph} onChange={(e) => patchCard({ [key]: e.target.value })} />
            </label>
          ))}
        </div>
      </div>

      <span className="muted">thème actif : {activeId}</span>
    </div>
  );
}

/** Éditeur de thème-couleurs : sélection du thème actif (config.activeThemeId) + édition
 *  des couleurs de ce thème (config.themes[id].styles.global). Options builtin servies par le serveur. */
const toColor = (v) => (/^#[0-9a-fA-F]{6}$/.test(v || '') ? v : '#000000');
const THEME_COLOR_KEYS = ['primaryColor', 'secondaryColor', 'backgroundColor', 'fontFamily'];
function ThemeEditor({ field, data, onChange, t }) {
  const activeId = data?.activeThemeId || 'default';
  const styles = data?.styles?.global || {};
  const builtins = field.options || [];
  const builtin = builtins.find((b) => b.value === activeId);
  // Couleur effective : édition dans config.styles.global (source des couleurs /display),
  // sinon défaut du thème builtin sélectionné.
  const colorOf = (key) => styles[key] ?? builtin?.colors?.[key] ?? '';

  // Clic sur un preset : applique sa palette à styles.global ET coordonne activeThemeId
  // (qui pilote les cartes de services + le thème du distributeur).
  const selectTheme = (b) => {
    onChange('activeThemeId', b.value);
    const c = b.colors || {};
    THEME_COLOR_KEYS.forEach((k) => { if (c[k]) onChange(`styles.global.${k}`, c[k]); });
  };
  const patchColor = (key, value) => onChange(`styles.global.${key}`, value);

  return (
    <div className="theme-editor">
      <div className="theme-list">
        {builtins.map((b) => (
          <button key={b.value} type="button" className={activeId === b.value ? 'theme-chip active' : 'theme-chip'} onClick={() => selectTheme(b)}>
            <span className="theme-swatch" style={{ background: b.colors?.primaryColor }} />
            <span className="theme-swatch" style={{ background: b.colors?.secondaryColor }} />
            {t.localized(b.label, b.value)}
          </button>
        ))}
      </div>
      <div className="theme-colors">
        {[['primaryColor', 'Principale'], ['secondaryColor', 'Secondaire'], ['backgroundColor', 'Fond']].map(([key, lbl]) => (
          <label key={key} className="color-field">
            <span>{lbl}</span>
            <input type="color" value={toColor(colorOf(key))} onChange={(e) => patchColor(key, e.target.value)} />
          </label>
        ))}
        <label className="color-field font">
          <span>Police</span>
          <input value={colorOf('fontFamily') || ''} placeholder="Arial, sans-serif" onChange={(e) => patchColor('fontFamily', e.target.value)} />
        </label>
      </div>
      <span className="muted">Preset sélectionné : {activeId} — ajuste les couleurs ci-dessus au besoin.</span>
    </div>
  );
}

/** Historique des versions de config + restauration (back : GET /history, POST /restore/:id). */
function ConfigHistory({ api, reload }) {
  const [versions, setVersions] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.getHistory()
    .then((v) => setVersions(Array.isArray(v) ? v : []))
    .catch((e) => { setVersions([]); setMsg(String(e?.response?.data?.error || e?.message || e)); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (id) => {
    if (!window.confirm('Restaurer cette version ? La configuration active sera remplacée.')) return;
    setBusy(true);
    setMsg('');
    try {
      await api.restore(id);
      setMsg('Version restaurée.');
      load();
      if (reload) reload();
    } catch (e) {
      setMsg('Échec : ' + String(e?.response?.data?.error || e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (versions === null) return <p className="muted">…</p>;
  return (
    <div className="config-history">
      {msg && <div className="status">{msg}</div>}
      {versions.length === 0 ? (
        <p className="muted">Aucune version enregistrée.</p>
      ) : (
        <table className="history-table">
          <thead><tr><th>Version</th><th>Date</th><th>État</th><th /></tr></thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className={v.isActive ? 'active' : ''}>
                <td>v{v.version}</td>
                <td>{v.createdAt ? new Date(v.createdAt).toLocaleString('fr-FR') : '—'}</td>
                <td>{v.isActive ? <span className="badge">active</span> : ''}</td>
                <td>{!v.isActive && <button type="button" disabled={busy} onClick={() => restore(v.id)}>Restaurer</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Journal d'audit d'activité des guichetiers : filtres + table paginée + export CSV.
 *  Lecture seule (GET /api/audit, réservé permission audit.view). */
const AUDIT_CATEGORIES = [
  ['', 'Toutes catégories'],
  ['ticket', 'Tickets'],
  ['counter', 'Guichets'],
  ['session', 'Présence (login/logout)'],
];
const AUDIT_ACTION_LABELS = {
  created: 'Créé', called: 'Appelé', recalled: 'Rappelé', paused: 'Pause',
  resumed: 'Repris', completed: 'Terminé', cancelled: 'Annulé',
  status: 'Statut guichet', login: 'Connexion', logout: 'Déconnexion',
};
function AuditLog({ api }) {
  const [filters, setFilters] = useState({ category: '', action: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const limit = 25;

  const params = () => {
    const p = { page, limit };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  };
  const load = () => {
    setBusy(true); setMsg('');
    api.getAudit(params())
      .then((d) => setData(d))
      .catch((e) => { setData({ entries: [], total: 0 }); setMsg(String(e?.response?.data?.error || e?.message || e)); })
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => { if (page !== 1) setPage(1); else load(); };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCsv = async () => {
    setMsg('');
    try {
      const blob = await api.downloadAuditCsv(params());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'ticketflow-audit.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('Export impossible : ' + String(e?.response?.data?.error || e?.message || e));
    }
  };

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="audit-log">
      <div className="audit-filters">
        <select value={filters.category} onChange={(e) => setF('category', e.target.value)}>
          {AUDIT_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input placeholder="action (ex. called)" value={filters.action} onChange={(e) => setF('action', e.target.value)} />
        <label>du <input type="date" value={filters.startDate} onChange={(e) => setF('startDate', e.target.value)} /></label>
        <label>au <input type="date" value={filters.endDate} onChange={(e) => setF('endDate', e.target.value)} /></label>
        <button type="button" onClick={applyFilters} disabled={busy}>Filtrer</button>
        <button type="button" className="ghost" onClick={exportCsv} disabled={busy || total === 0}>⬇ CSV</button>
      </div>
      {msg && <div className="status">{msg}</div>}
      {data === null ? <p className="muted">…</p> : total === 0 ? (
        <p className="muted">Aucune activité enregistrée.</p>
      ) : (
        <>
          <table className="perm-matrix audit-table">
            <thead><tr><th>Date/heure</th><th>Action</th><th>Ticket</th><th>Service</th><th>Guichet</th><th>Agent</th><th>État</th></tr></thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id}>
                  <td>{e.at ? new Date(e.at).toLocaleString('fr-FR') : '—'}</td>
                  <td>{AUDIT_ACTION_LABELS[e.action] || e.action}</td>
                  <td>{e.ticketNumber ?? '—'}</td>
                  <td>{e.serviceName ?? e.serviceType ?? '—'}</td>
                  <td>{e.counterName ?? (e.counterId != null ? `Guichet ${e.counterId}` : '—')}</td>
                  <td>{e.agentName ?? e.agentId ?? '—'}</td>
                  <td>{e.status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="audit-pager">
            <button type="button" className="ghost" onClick={() => setPage((n) => Math.max(1, n - 1))} disabled={busy || page <= 1}>‹ Précédent</button>
            <span className="muted">page {page} / {pages} — {total} entrées</span>
            <button type="button" className="ghost" onClick={() => setPage((n) => Math.min(pages, n + 1))} disabled={busy || page >= pages}>Suivant ›</button>
          </div>
        </>
      )}
    </div>
  );
}

/** Réglage des annonces vocales AR/FR/EN : activation par langue + choix de la voix
 *  (parmi les voix installées SUR CETTE MACHINE), débit/hauteur/volume, test.
 *  Écrit une PRÉFÉRENCE centrale (config.voice.*) ; l'écran /display la résout
 *  contre SES voix. L'installation des voix OS est hors app (voir consignes). */
const VOICE_LANGS = [['fr', 'Français', 'fr'], ['ar', 'العربية', 'ar'], ['en', 'English', 'en']];
const VOICE_SAMPLE = { fr: 'Ticket numéro 42, guichet 3', ar: 'التذكرة رقم ٤٢، الشباك ٣', en: 'Ticket number 42, counter 3' };
function VoiceSettings({ data, onChange }) {
  const [voices, setVoices] = useState([]);
  const [detected, setDetected] = useState(false);
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const voice = data?.voice || {};
  const langs = voice.languages || {};

  const loadVoices = () => {
    if (!synth) return;
    setVoices(synth.getVoices() || []);
    setDetected(true);
  };
  useEffect(() => {
    if (!synth) return;
    loadVoices();
    synth.onvoiceschanged = loadVoices;
    return () => { if (synth) synth.onvoiceschanged = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const forLang = (code) => voices.filter((v) => (v.lang || '').toLowerCase().startsWith(code));
  const set = (lang, key, val) => onChange(`voice.languages.${lang}.${key}`, val);

  const test = (lang) => {
    if (!synth) return;
    const cfg = langs[lang] || {};
    const u = new SpeechSynthesisUtterance(VOICE_SAMPLE[lang]);
    const v = voices.find((x) => x.voiceURI === cfg.voiceURI) || forLang(lang)[0];
    if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = cfg.locale || lang; }
    // volume : Number(undefined) = NaN, et NaN ?? 1 = NaN (?? ne teste que null/undefined) → muet.
    // On garde un 0 explicite mais on retombe sur 1 si NaN.
    const vol = Number(cfg.volume);
    u.rate = Number(cfg.rate) || 1; u.pitch = Number(cfg.pitch) || 1; u.volume = Number.isFinite(vol) ? vol : 1;
    synth.cancel(); synth.speak(u);
  };

  if (!synth) return <p className="muted">Synthèse vocale indisponible dans cet environnement.</p>;

  return (
    <div className="voice-settings">
      <label className="field-inline">
        <input type="checkbox" checked={voice.enabled !== false} onChange={(e) => onChange('voice.enabled', e.target.checked)} />
        Annonces vocales activées
      </label>
      <div className="voice-tools">
        <button type="button" className="ghost" onClick={loadVoices}>🔄 Détecter les voix</button>
        <span className="muted">{detected ? `${voices.length} voix sur cette machine` : '…'}</span>
      </div>

      {VOICE_LANGS.map(([lang, label, code]) => {
        const cfg = langs[lang] || {};
        const list = forLang(code);
        const missing = detected && list.length === 0;
        return (
          <div key={lang} className={`voice-lang${cfg.enabled ? ' on' : ''}`}>
            <div className="voice-lang-head">
              <label className="field-inline">
                <input type="checkbox" checked={Boolean(cfg.enabled)} onChange={(e) => set(lang, 'enabled', e.target.checked)} />
                <b>{label}</b> <span className="muted">({lang})</span>
              </label>
              {missing && <span className="voice-missing">⚠ aucune voix {lang} installée sur ce poste</span>}
            </div>
            {cfg.enabled && (
              <div className="voice-lang-body">
                <label className="voice-field">
                  <span>Voix</span>
                  <select value={cfg.voiceURI || ''} onChange={(e) => set(lang, 'voiceURI', e.target.value)}>
                    <option value="">(défaut du poste)</option>
                    {list.map((v) => <option key={v.voiceURI} value={v.voiceURI}>{v.name} — {v.lang}</option>)}
                  </select>
                </label>
                {[['rate', 'Débit', 0.5, 2], ['pitch', 'Hauteur', 0, 2], ['volume', 'Volume', 0, 1]].map(([k, lbl, min, max]) => (
                  <label key={k} className="voice-field">
                    <span>{lbl} ({Number(cfg[k] ?? (k === 'volume' ? 1 : 1)).toFixed(1)})</span>
                    <input type="range" min={min} max={max} step="0.1"
                      value={cfg[k] ?? (k === 'volume' ? 1 : 1)}
                      onChange={(e) => set(lang, k, Number(e.target.value))} />
                  </label>
                ))}
                <button type="button" className="ghost" onClick={() => test(lang)}>▶ Tester</button>
              </div>
            )}
          </div>
        );
      })}
      <p className="muted voice-note">
        Les voix listées sont celles installées sur <b>cette machine</b> ; l'écran /display
        utilise les voix de <b>son</b> poste. Pour installer des voix manquantes (AR/FR/EN),
        suivre <code>docs/19-CONSIGNES-VOIX-TTS-MULTILINGUE.md</code> — l'installation d'une
        voix est une opération système, non automatisable depuis l'admin.
      </p>
    </div>
  );
}

/** Connexion au plan de contrôle central : le manager saisit URL + id de site +
 *  token (provisionné au central) et connecte/déconnecte. Réglage LOCAL au serveur
 *  (API /control-plane), token jamais réaffiché. */
function ControlPlaneConfig({ api }) {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ url: '', siteId: '', token: '', suiviUrl: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    api.getControlPlane()
      .then((d) => {
        if (!alive) return;
        setSt(d);
        setForm({
          url: d.url || '', siteId: d.siteId || '', token: '',
          // Pré-rempli : c'est l'adresse de NOTRE service de suivi, la même pour tous les
          // sites. La laisser vide obligerait chaque exploitant à la retaper sans erreur.
          suiviUrl: d.suiviUrl || 'https://ticket-suivi.amia.fr',
        });
      })
      .catch((e) => setMsg(String(e?.response?.data?.error || e?.message || e)));
    const t = setInterval(() => api.getControlPlane().then((d) => alive && setSt(d)).catch(() => {}), 5000);
    return () => { alive = false; clearInterval(t); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true); setMsg('');
    try {
      const d = await api.saveControlPlane({
        enabled: true, url: form.url.trim(), siteId: form.siteId.trim(),
        ...(form.token ? { token: form.token } : {}),
        // Le suivi voyage avec le reste : c'est un seul geste d'exploitation, sur un seul
        // écran, avec une seule identité de site.
        suiviUrl: form.suiviUrl.trim(),
      });
      setSt(d); setForm((f) => ({ ...f, token: '' }));
    } catch (e) { setMsg(String(e?.response?.data?.error || e?.message || e)); }
    finally { setBusy(false); }
  };
  /**
   * Activer / couper le SUIVI CLIENT sans toucher au reste.
   *
   * On envoie l'URL avec la bascule : un exploitant qui coche « suivi » après avoir tapé
   * l'adresse sans cliquer « Connecter » attend que cela suffise. Le jeton, lui, n'est pas
   * renvoyé — c'est celui du central qui sert, sauf jeton propre déjà enregistré.
   */
  const basculerSuivi = async (actif) => {
    setBusy(true); setMsg('');
    try {
      setSt(await api.saveControlPlane({
        enabled: true, url: form.url.trim(), siteId: form.siteId.trim(),
        suiviEnabled: actif, suiviUrl: form.suiviUrl.trim(),
      }));
    } catch (e) { setMsg(String(e?.response?.data?.error || e?.message || e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true); setMsg('');
    try { setSt(await api.disconnectControlPlane()); }
    catch (e) { setMsg(String(e?.response?.data?.error || e?.message || e)); }
    finally { setBusy(false); }
  };

  if (!st) return <p className="muted">…</p>;
  return (
    <div className="control-plane">
      <div className="cp-status">
        État :{' '}
        {st.connected ? <span className="badge">connecté</span>
          : st.enabled ? <span className="muted">connexion en cours…</span>
          : <span className="muted">inactif</span>}
        {st.lastError && !st.connected && <span className="voice-missing"> — {st.lastError}</span>}
      </div>
      <div className="cp-form">
        <label className="voice-field"><span>URL du central</span>
          <input value={form.url} placeholder="https://ticket-admin.amia.fr" onChange={(e) => setForm({ ...form, url: e.target.value })} /></label>
        <label className="voice-field"><span>Identifiant de site</span>
          <input value={form.siteId} placeholder="cabinet-alger" onChange={(e) => setForm({ ...form, siteId: e.target.value })} /></label>
        <label className="voice-field"><span>Token de connexion {st.hasToken ? '(enregistré)' : ''}</span>
          <input type="password" value={form.token}
            placeholder={st.hasToken ? '•••• (laisser vide pour garder)' : 'coller le token'}
            onChange={(e) => setForm({ ...form, token: e.target.value })} /></label>
      </div>
      <div className="cp-actions">
        <button type="button" onClick={connect} disabled={busy || !form.url || !form.siteId || (!st.hasToken && !form.token)}>Connecter</button>
        <button type="button" className="ghost" onClick={disconnect} disabled={busy || !st.enabled}>Déconnecter</button>
      </div>
      {msg && <div className="status">{msg}</div>}
      <p className="muted">Le token est fourni par la console centrale (provisioning) ; il n'est jamais réaffiché.</p>

      {/* ─── SUIVI CLIENT ─────────────────────────────────────────────────────
          Second canal SORTANT du site, vers le service que le client atteint en
          scannant le QR de son ticket. Même identité de site, même jeton : le service
          de suivi est en lecture et ne renvoie aucun ordre au serveur. */}
      <div className="cp-suivi" style={{ marginTop: '1.2rem', paddingTop: '1rem', borderTop: '1px solid var(--line)' }}>
        <div className="cp-status">
          Suivi client :{' '}
          {st.suiviConnecte ? <span className="badge">connecté</span>
            : st.suiviEnabled ? <span className="muted">connexion en cours…</span>
            : <span className="muted">inactif</span>}
          {st.suiviErreur && !st.suiviConnecte && <span className="voice-missing"> — {st.suiviErreur}</span>}
        </div>
        <div className="cp-form">
          <label className="voice-field"><span>URL du site de suivi</span>
            <input value={form.suiviUrl} placeholder="https://ticket-suivi.amia.fr"
              onChange={(e) => setForm({ ...form, suiviUrl: e.target.value })} /></label>
        </div>
        <div className="cp-actions">
          <button type="button" onClick={() => basculerSuivi(true)}
            disabled={busy || !form.suiviUrl || !form.siteId || !st.hasToken}>Activer le suivi</button>
          <button type="button" className="ghost" onClick={() => basculerSuivi(false)}
            disabled={busy || !st.suiviEnabled}>Couper le suivi</button>
        </div>
        <p className="muted">
          Le client scanne le QR de son ticket et voit sa position. Le serveur POUSSE l'état
          de sa file (numéros, ordre, guichet) — aucun nom, aucune donnée personnelle, et le
          service de suivi ne peut rien commander en retour. Le jeton du central est réutilisé.
        </p>
      </div>
    </div>
  );
}

/**
 * Bouton d'ACTION générique (non lié aux données) : appelle un endpoint via
 * `api.raw` (relayé au site en mode console). Ex. réinitialisation des compteurs.
 * Champ : { type:'action', endpoint, method?, body?, button, confirm?, success? }.
 */
function ActionButton({ field, api, reload, t }) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const run = async () => {
    const confirmMsg = field.confirm && t.localized(field.confirm, '');
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      const method = String(field.method || 'post').toLowerCase();
      await api.raw[method](field.endpoint, field.body || {});
      setMsg(t.localized(field.success, '✓'));
      await reload?.();
    } catch (e) {
      setMsg(e?.response?.status === 403 ? 'Accès refusé (rôle)' : String(e?.response?.data?.error || e?.message || e));
    } finally { setBusy(false); }
  };
  return (
    <div className="action-field">
      <button onClick={run} disabled={busy}>{busy ? '…' : t.localized(field.button || field.label, 'Action')}</button>
      {msg && <span className="muted" style={{ marginInlineStart: '.6rem' }}>{msg}</span>}
    </div>
  );
}

function Field({ field, data, onChange, api, reload, t }) {
  const value = getPath(data, field.path);
  const label = t.localized(field.label, field.path);
  const set = (v) => onChange(field.path, v);

  if (field.type === 'action') {
    return (
      <div className="field">
        {field.label && <label>{label}</label>}
        <ActionButton field={field} api={api} reload={reload} t={t} />
      </div>
    );
  }

  if (field.type === 'repeater') {
    return (
      <div className="field">
        <label>{label}</label>
        <Repeater field={field} value={value} onChange={set} t={t} />
      </div>
    );
  }

  if (field.type === 'mediaUpload') {
    return (
      <div className="field">
        <label>{label}</label>
        <MediaUpload field={field} api={api} reload={reload} t={t} />
      </div>
    );
  }

  if (field.type === 'imageUpload') {
    return (
      <div className="field">
        <label>{label}</label>
        <ImageUpload field={field} value={value} onChange={set} api={api} t={t} />
      </div>
    );
  }

  if (field.type === 'permissionMatrix') {
    return (
      <div className="field">
        <label>{label}</label>
        <PermissionMatrix field={field} value={value} onChange={set} t={t} />
      </div>
    );
  }

  if (field.type === 'usersManager') {
    return (
      <div className="field">
        <label>{label}</label>
        <UsersManager api={api} />
      </div>
    );
  }

  if (field.type === 'apiView') {
    return (
      <div className="field">
        <label>{label}</label>
        <ApiView field={field} api={api} />
      </div>
    );
  }

  if (field.type === 'themeEditor') {
    return (
      <div className="field">
        <label>{label}</label>
        <ThemeEditor field={field} data={data} onChange={onChange} t={t} />
      </div>
    );
  }

  if (field.type === 'serviceCardShape') {
    return (
      <div className="field">
        <label>{label}</label>
        <ServiceCardShape field={field} data={data} onChange={set} t={t} />
      </div>
    );
  }

  if (field.type === 'configHistory') {
    return (
      <div className="field">
        <label>{label}</label>
        <ConfigHistory api={api} reload={reload} />
      </div>
    );
  }

  if (field.type === 'auditLog') {
    return (
      <div className="field">
        <label>{label}</label>
        <AuditLog api={api} />
      </div>
    );
  }

  if (field.type === 'voiceSettings') {
    return (
      <div className="field">
        <label>{label}</label>
        <VoiceSettings data={data} onChange={onChange} />
      </div>
    );
  }

  if (field.type === 'controlPlane') {
    return (
      <div className="field">
        <label>{label}</label>
        <ControlPlaneConfig api={api} />
      </div>
    );
  }

  const inline = field.type === 'boolean';
  return (
    <div className={inline ? 'field field-inline' : 'field'}>
      <label>
        {inline && <InputControl type={field.type} value={value} onChange={set} t={t} />} {label}
      </label>
      {!inline && <InputControl type={field.type} value={value} options={field.options} onChange={set} t={t} />}
    </div>
  );
}

export default function SchemaForm({ section, data, onChange, api, reload, t }) {
  return (
    <div className="schema-form">
      <h2>{t.localized(section.label, section.id)}</h2>
      {(section.fields || []).map((f, i) => (
        <Field key={`${f.type}:${f.path}:${i}`} field={f} data={data} onChange={onChange} api={api} reload={reload} t={t} />
      ))}
    </div>
  );
}
